import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, debounceTime, firstValueFrom, map, Observable, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { PresencePerMonthListDataSource, PresencePerMonthViewModel } from './presence-per-month-list.datasource';
import { NgModel, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Filter } from 'src/app/models/common/filter';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { SubSink } from 'SubSink';
import { MatDatepicker } from '@angular/material/datepicker';
import { Moment } from 'moment';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { TimeSpan } from 'src/app/common/model/time-span';
import { animate, style, transition, trigger } from '@angular/animations';
import { FilterPresetDto } from 'src/app/models/dtos/filter-preset-dto';
import { FilterTypeEnum } from 'src/app/models/enums/filter-type-enum';
import { UserService } from 'src/app/data/user.service';
import { FilterPresetNameFormDialogComponent } from 'src/app/shared/components/filters/filter-preset-name-form-dialog/filter-preset-name-form-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { PresencePerMonthListFiltersComponent } from '../presence-per-month-list-filters/presence-per-month-list-filters.component';
import { buildFilterArray } from 'src/app/common/utils/build-filter-array';
import Comparator from 'src/app/common/comparators/comparator';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import moment from 'moment';
import { TimesheetService } from 'src/app/data/timesheet.service';
import { Permission } from 'src/app/common/enums';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { ScreenService } from 'src/app/core/screen.service';
import { daysInMonth } from 'src/app/common/utils/date.utils';

export interface MonthWorkingTime {
  days: number,
  hours: number
}

@Component({
  selector: 'app-presence-per-month-list',
  templateUrl: './presence-per-month-list.component.html',
  styleUrl: './presence-per-month-list.component.scss',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('1s ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('1s ease-in', style({ opacity: 0 }))
      ])
    ])
  ],
  standalone: false
})
export class PresencePerMonthListComponent implements AfterViewInit, OnInit, OnDestroy {
  dataSource = inject(PresencePerMonthListDataSource);
  screenService = inject(ScreenService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private datePipe = inject(DatePipe);
  private spinner = inject(NgxSpinnerService);
  private dictionaryService = inject(DictionaryService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private formBuilder = inject(UntypedFormBuilder);
  private timesheetService = inject(TimesheetService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private authService = inject(AuthService);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('filterSection', { static: false }) filterSection!: ElementRef;

  monthWorkingTime$: Observable<MonthWorkingTime>;
  monthWorkingTimeTicks$: Observable<number>;

  set currentDate(value: Date) {
    this.currentDate$.next(value);
  }
  get currentDate() {
    return this.currentDate$.value;
  }

  private currentDate$ = new BehaviorSubject<Date>(new Date());
  public daysInMonth$ = this.currentDate$.pipe(map(cd => daysInMonth(cd.getMonth(), cd.getFullYear())));

  filtersFormGroup: UntypedFormGroup;
  filters: Filter[] = [];
  areFiltersExpanded: boolean = false;
  displayedColumns = [
    'workerFullName',
    'pesel',
    'company',
    'employerObject',
    'employmentType',
    'employmentDateFrom',
    'employmentDateTo',
    'totalTimespan',
    'totalTimespanDay',
    'totalTimespanNight',
    'workedDays',
    'correctedTotalTimespan',
    'correctedTotalTimespanDay',
    'correctedTotalTimespanNight',
    'correctedWorkedDays',
    'totalDaysConcludedHomeOfficeRemote',
    'totalDaysHomeOfficeOnDemand',
    'actions'
  ];
  listHeight = '100vh';

  public readonly maxHoursDigitCount = 3;
  public readonly maxMinuteTensLimit = 6;
  public readonly minutesSeparator = ':';

  public inputTransformFn = (value: string | number): string => {
    if (value == null) {
      return undefined;
    }

    if (typeof value === 'string') {
      if (value.length > this.maxHoursDigitCount && !value.includes(this.minutesSeparator) && value[this.maxHoursDigitCount] !== this.minutesSeparator) {
        value = [value.slice(0, this.maxHoursDigitCount), this.minutesSeparator, value.slice(this.maxHoursDigitCount)].join();
      }

      const splitted = value.split(this.minutesSeparator);

      if (splitted && splitted.length === 2 && +splitted[1][0] >= this.maxMinuteTensLimit) {
        return splitted[0] + this.minutesSeparator;
      }
    } else if (!isNaN(Number(value))) {
      const ts = TimeSpan.fromTicks(value)
      return `${Math.floor(ts.totalHours)}:${String(ts.minutes).padStart(2, '0')}`;
    }

    return value?.toString();
  }

  public outputTransformFn = (value: string | number | null | undefined): number => {
    if (value == null || value === '') {
      return undefined;
    }

    const v = +value;

    let hourTicks = Math.floor(v) * 60 * 60 * 10e6;
    let minTicks = (v % 1) * 100 * 60 * 10e6;
    return Math.round(hourTicks + minTicks);
  };

  onBlurLeadZero(input: NgModel) {
    input.control.setValue(input.control.value);
  }

  private readonly subs = new SubSink();
  private readonly hoursPerWorkingDay = 8;

  public readonly defaultFilterPresetName = 'default';
  public filterPresets: FilterPresetDto[];
  private _selectedFilterPresetId: number;
  public set selectedFilterPresetId(value: number) {
    if (this._selectedFilterPresetId !== value) {
      this._selectedFilterPresetId = value;

      if (value) {
        this.applyFilterPreset(this.filterPresets.find(fp => fp.Id === value));
      }
    }
  }

  public get selectedFilterPresetId(): number {
    return this._selectedFilterPresetId;
  }
  public get selectedFilterPreset(): FilterPresetDto {
    return this.filterPresets?.find(fp => fp.Id === this.selectedFilterPresetId);
  }
  public get showFilterPresetSelector(): boolean {
    return this.filterPresets && (this.filterPresets.length > 1 || (this.filterPresets.length === 1 && this.filterPresets[0].Name !== this.defaultFilterPresetName));
  }
  public get isSavedFilterPreset(): boolean {
    return this.selectedFilterPreset && this.selectedFilterPreset.Name !== this.defaultFilterPresetName;
  }

  constructor() { }

  @HostListener('window:resize')
  onResize() {
    this.adjustListHeight();
  }

  ngOnInit(): void {
    this.currentDate = moment(this.router.url.replace('/presences/', ''), "YYYY/MM").toDate();
    this.buildFormGroup();
    this.initFiltersFormChangeObserver();
    this.initLoader();
    this.getMonthWorkingHours();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.restoreSavedFiltersPreset();
    this.filters = buildFilterArray(this.filtersFormGroup, PresencePerMonthListFiltersComponent.operatorsMap);
    this.subs.sink = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subs.sink = this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0));

    this.subs.sink = combineLatest([
      this.sort.sortChange.pipe(startWith(null)),
      this.paginator.page.pipe(startWith(null)),
      this.translateService.onLangChange.pipe(startWith(null))
    ])
      .subscribe(() => this.fetchPresences());

    this.onResize();
  }

  previousMonth() {
    this.currentDate = new Date(this.currentDate.addMonths(-1));
    this.setNavigation();
    this.fetchPresences();
  }

  nextMonth() {
    this.currentDate = new Date(this.currentDate.addMonths(1));
    this.setNavigation();
    this.fetchPresences();
  }

  onDateChange(event: Moment, picker?: MatDatepicker<Moment>): void {
    picker.close();
    this.currentDate = event.toDate();
    this.setNavigation();
    this.fetchPresences();
  }

  resetFilters(): void {
    this.filtersFormGroup.reset();
  }

  filterData(filters: Filter[]) {
    this.filters = filters;
    this.fetchPresences();
  }

  toggleFiltersPanel() {
    this.areFiltersExpanded = !this.areFiltersExpanded;
    setTimeout(() => this.adjustListHeight(), 50);
  }

  adjustListHeight() {
    const screenHeight = window.innerHeight;
    const filterHeight = this.filterSection?.nativeElement?.offsetHeight || 0;
    const offset = 450;

    this.listHeight = `${screenHeight - filterHeight - offset}px`;

    this.changeDetectorRef.detectChanges();
  }

  hasPermissionToSetCorrections() {
    return this.authService.hasPermission(Permission.SetAllTimesheetCorrection) || this.authService.hasPermission(Permission.SetMyExternalWorkerTimesheetCorrection) || this.authService.hasPermission(Permission.Supervisor);
  }

  private initLoader() {
    this.subs.sink = this.dataSource.isLoading$
      .subscribe(isLoading => isLoading ? this.spinner.show() : this.spinner.hide());
  }

  private setNavigation() {
    this.router.navigateByUrl(`presences/${this.datePipe.transform(this.currentDate, 'yyyy/MM')}`);
  }

  private fetchPresences(): void {
    this.dataSource.pageOptionsSubject.next({
      Year: this.currentDate.getFullYear(),
      Month: this.currentDate.getMonth() + 1,
      Page: this.paginator.pageIndex + 1,
      PageSize: this.paginator.pageSize,
      SortingField: this.sort.active,
      SortingDirection: this.sort.direction,
      Filters: this.filters
    });
  }

  private getMonthWorkingHours() {
    this.monthWorkingTimeTicks$ = this.currentDate$
      .pipe(switchMap(date => this.dictionaryService.getMonthWorkingHours(date.getFullYear(), date.getMonth() + 1)))
      .pipe(shareReplay());

    this.monthWorkingTime$ = this.monthWorkingTimeTicks$
      .pipe(map(ticks => {
        const timeSpan = TimeSpan.fromTicks(ticks);
        return {
          days: timeSpan.totalHours / this.hoursPerWorkingDay,
          hours: timeSpan.totalHours
        }
      }));
  }

  private applyFilterPreset(filterPreset) {
    const filters = filterPreset?.Object;

    if (filters && Object.values(filters).some((v) => !!v)) {
      this.filtersFormGroup.reset(undefined, { emitEvent: false });
      this.filtersFormGroup.patchValue(filters, { emitEvent: false });
      this.areFiltersExpanded = this.areFiltersExpanded || this.selectedFilterPreset.Name === this.defaultFilterPresetName;
    }
  }

  private async upsertFilterPreset(): Promise<void> {
    const selectedPreset = this.filterPresets.find(fp => (this.selectedFilterPresetId && this.selectedFilterPresetId === fp.Id) || fp.Name === this.defaultFilterPresetName);

    const filters = this.nonNullValues(this.filtersFormGroup.getRawValue());

    if (selectedPreset) {
      if (filters) {
        await firstValueFrom(this.userService.updateFilterPreset(selectedPreset.Id, {
          Name: selectedPreset.Name,
          FilterPresetObject: filters
        }));
      } else {
        await firstValueFrom(this.userService.deleteFilterPreset(selectedPreset.Id));
      }
    } else if (filters) {
      await firstValueFrom(this.userService.addFilterPreset({
        Name: this.defaultFilterPresetName,
        FilterTypeId: FilterTypeEnum.PresencePerMonthList,
        FilterPresetObject: filters
      }));
    } else {
      return;
    }
    this.filterPresets = await firstValueFrom(this.userService.getFilterPresets(FilterTypeEnum.PresencePerMonthList));
    this.selectedFilterPresetId = this.filterPresets?.find(fp => fp.IsSelected)?.Id;
  }

  private nonNullValues(obj): { [key: string]: unknown } {
    const properties = Object.entries(obj).filter(([key, value]) => value !== null && value && (!(value instanceof Array) || value.length));
    if (properties.length) {
      return Object.fromEntries(properties);
    }
  }

  private async restoreSavedFiltersPreset() {
    this.filterPresets = await firstValueFrom(this.userService.getFilterPresets(FilterTypeEnum.PresencePerMonthList));

    if (this.filterPresets && this.filterPresets.length) {
      const selectedPreset = this.filterPresets.find(fp => fp.IsSelected);

      this.selectedFilterPresetId = selectedPreset?.Id;
    }
  }

  public async saveFilterPresetButtonClick(): Promise<void> {
    if (this.isSavedFilterPreset) {

      const existingDefaultPreset = this.filterPresets.find(fp => fp.Name == this.defaultFilterPresetName && this.selectedFilterPresetId !== fp.Id);

      if (existingDefaultPreset) {
        await firstValueFrom(this.userService.deleteFilterPreset(existingDefaultPreset.Id));
      }

      this.selectedFilterPreset.Name = this.defaultFilterPresetName;
      await this.upsertFilterPreset();
    } else {
      var res = await firstValueFrom(this.dialog
        .open(FilterPresetNameFormDialogComponent, { panelClass: 'form-dialog' })
        .afterClosed());

      if (res) {
        if (this.selectedFilterPreset?.Name === this.defaultFilterPresetName) {
          this.selectedFilterPreset.Name = res;
        }
        await this.upsertFilterPreset();
      }
    }
  }

  public async setSelectedFilterPresetId(value: number): Promise<void> {
    this.selectedFilterPresetId = value;
    await this.filterData(buildFilterArray(this.filtersFormGroup, PresencePerMonthListFiltersComponent.operatorsMap));
    await firstValueFrom(this.userService.selectFilterPreset(value));
  }

  private buildFormGroup(): void {
    this.filtersFormGroup = this.formBuilder.group({
      companyId: [null],
      company: [null, [autocompleteValidator]],
      employerObjectId: [null],
      employerObject: [null, [autocompleteValidator]],
      firstName: [null],
      lastName: [null],
      employmentDateFrom: [null],
      employmentDateTo: [null],
      hasHomeOfficeOnDemand: [null],
      hasConcludedHomeOfficeRemote: [null],
      hasCorrectionWorkingHours: [null],
      hasCorrectionDayWorkingHours: [null],
      hasCorrectionNightWorkingHours: [null],
      hasCorrectionWorkedDays: [null],
      pesel: [null],
      employmentTypeId: [null]
    });
  }

  private initFiltersFormChangeObserver() {
    const comparator = new Comparator<any>();

    this.subs.sink = this.filtersFormGroup.valueChanges
      .pipe(debounceTime(1000))
      .subscribe(async () => {
        if (this.filtersFormGroup.invalid) {
          return;
        }

        const filters = this.nonNullValues(this.filtersFormGroup.getRawValue());

        const selectedFilterPresetId = this.filterPresets.find(fp => comparator.equals(fp.Object, filters))?.Id;

        if (!selectedFilterPresetId) {
          this.selectedFilterPresetId = this.filterPresets.find(fp => fp.Name === this.defaultFilterPresetName)?.Id;

          await this.upsertFilterPreset();
        } else {
          this.selectedFilterPresetId = selectedFilterPresetId;
        }
      });
  }

  saveTotalTimespanDayCorrection(value: number | undefined, timesheet: PresencePerMonthViewModel) {
    if (timesheet.OriginCorrectedTotalTimespanDay === (value ?? null)) {
      timesheet.OriginCorrectedTotalTimespanDay = undefined;
    } else if (timesheet.CorrectedTotalTimespanDay !== value && timesheet.OriginCorrectedTotalTimespanDay === undefined) {
      timesheet.OriginCorrectedTotalTimespanDay = timesheet.CorrectedTotalTimespanDay ?? null;
    }

    timesheet.CorrectedTotalTimespanDay = value;

    timesheet.CorrectedTotalTimespan = (timesheet.CorrectedTotalTimespanDay ?? 0) + (timesheet.CorrectedTotalTimespanNight ?? 0);
  }

  saveTotalTimespanNightCorrection(value: number | undefined, timesheet: PresencePerMonthViewModel) {
    if (timesheet.OriginCorrectedTotalTimespanNight === (value ?? null)) {
      timesheet.OriginCorrectedTotalTimespanNight = undefined;
    } else if (timesheet.CorrectedTotalTimespanNight !== value && timesheet.OriginCorrectedTotalTimespanNight === undefined) {
      timesheet.OriginCorrectedTotalTimespanNight = timesheet.CorrectedTotalTimespanNight ?? null;
    }

    timesheet.CorrectedTotalTimespanNight = value;

    timesheet.CorrectedTotalTimespan = (timesheet.CorrectedTotalTimespanDay ?? 0) + (timesheet.CorrectedTotalTimespanNight ?? 0);
  }

  saveWorkingDaysCorrection(value: number | undefined, timesheet: PresencePerMonthViewModel) {
    if (timesheet.OriginCorrectedWorkedDays === (value ?? null)) {
      timesheet.OriginCorrectedWorkedDays = undefined;
    } else if (timesheet.CorrectedWorkedDays !== value && timesheet.OriginCorrectedWorkedDays === undefined) {
      timesheet.OriginCorrectedWorkedDays = timesheet.CorrectedWorkedDays ?? null;
    }

    timesheet.CorrectedWorkedDays = value;
  }

  setCorrectionEnabled(element: PresencePerMonthViewModel) {
    const nightTimeChanged = element.CorrectedTotalTimespanNight !== null && element.CorrectedTotalTimespanNight !== undefined;
    const dayTimeChanged = element.CorrectedTotalTimespanDay !== null && element.CorrectedTotalTimespanDay !== undefined;
    const workedDaysChanged = element.CorrectedWorkedDays !== null && element.CorrectedWorkedDays !== undefined

    return (element.IsUpToDate ||
      (!element.IsUpToDate && element.CorrectedTotalTimespanNight === null && element.CorrectedTotalTimespanNight === null && element.CorrectedWorkedDays === null)) &&
      ((element.OriginCorrectedTotalTimespanDay === undefined && element.OriginCorrectedTotalTimespanNight === undefined && element.OriginCorrectedWorkedDays === undefined) ||
        nightTimeChanged !== dayTimeChanged || nightTimeChanged !== workedDaysChanged
      );
  }

  async submit(timesheet: PresencePerMonthViewModel) {
    if (timesheet.OriginCorrectedWorkedDays !== null && timesheet.OriginCorrectedWorkedDays && !timesheet.CorrectedWorkedDays) {
      const confirmResult = await this.openConfirmDialog(this.translateService.instant('PresencePerMonthList.ApproveClearCorrectionWarnning'));

      if (!confirmResult) {
        return;
      }
    } else if (timesheet.CorrectedTotalTimespan !== await firstValueFrom(this.monthWorkingTimeTicks$) || timesheet.CorrectedWorkedDays !== await firstValueFrom(this.monthWorkingTime$.pipe(map(mwt => mwt.days)))) {
      const timespanPerDay = timesheet.CorrectedTotalTimespan / timesheet.CorrectedWorkedDays;
      const hours = Math.floor((timespanPerDay / (60 * 60 * 10e6)));
      const minutes = Math.round((timespanPerDay / (60 * 10e6)) % 60);

      let workedTime = this.translateService.instant('PresencePerMonthList.ApproveCorrectionWarnningHours', { hours });

      if (minutes) {
        workedTime += ' ';
        workedTime += this.translateService.instant('PresencePerMonthList.ApproveCorrectionWarnningMinutes', { minutes });
      }

      const confirmResult = await this.openConfirmDialog(this.translateService.instant('PresencePerMonthList.ApproveCorrectionWarnning', { workedTime }));

      if (!confirmResult) {
        return;
      }
    }

    const originCorrectedTotalTimespanDay = timesheet.OriginCorrectedTotalTimespanDay;
    const originCorrectedTotalTimespanNight = timesheet.OriginCorrectedTotalTimespanNight;
    const originCorrectedWorkedDays = timesheet.OriginCorrectedWorkedDays;
    const isUpToDate = timesheet.IsUpToDate;

    timesheet.OriginCorrectedTotalTimespanDay = undefined;
    timesheet.OriginCorrectedTotalTimespanNight = undefined;
    timesheet.OriginCorrectedWorkedDays = undefined;
    timesheet.IsUpToDate = true;

    this.changeDetectorRef.detectChanges();

    await firstValueFrom(this.timesheetService.setCorrections({
      WorkerAgreementId: timesheet.WorkerAgreementId,
      Month: this.currentDate.getMonth() + 1,
      Year: this.currentDate.getFullYear(),
      CorrectedTotalTimespanDay: timesheet.CorrectedTotalTimespanDay,
      CorrectedTotalTimespanNight: timesheet.CorrectedTotalTimespanNight,
      CorrectedWorkedDays: timesheet.CorrectedWorkedDays
    })
      .pipe(tap({
        next: t => this.dataSource.updateItem(t),
        error: () => {
          timesheet.OriginCorrectedTotalTimespanDay = originCorrectedTotalTimespanDay;
          timesheet.OriginCorrectedTotalTimespanNight = originCorrectedTotalTimespanNight;
          timesheet.OriginCorrectedWorkedDays = originCorrectedWorkedDays;
          timesheet.IsUpToDate = isUpToDate;
          this.changeDetectorRef.detectChanges();
        }
      })));
  }

  private openConfirmDialog(message: string, title: string = null, confirmButtonLabel: string = null, dismissButtonLabel: string = null): Promise<boolean> {
    return firstValueFrom(this.dialog.open(ConfirmDialogComponent, {
      data: new ConfirmDialogData(title, message, confirmButtonLabel, dismissButtonLabel),
      panelClass: 'confirm-dialog'
    }).afterClosed());
  }
}